import { useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { TOWN_LOCATIONS } from '../rooms';
import { INTRO_RIDE, RIDE_SPEEDS } from '../worldRide';
import { emitMove } from '../services/worldSocket';

// RideSequence — the car, as a camera behaviour (Build_Game_Intro §4.2).
//
// While worldStore.ride is set this drives the street camera along the tour
// loop: position follows the polyline, the view eases along the tangent, and
// each flagged route point fires its landmark reveal exactly once (the tour IS
// the map tutorial). When the scene closes, SceneDock requests the drop-off
// and this glides to the salon kerb and hands the camera back.
//
// CAMERA OWNERSHIP IS ABSOLUTE. WalkControls returns early while `ride` is
// set and re-adopts the camera's heading when it clears — the spec's repeated
// warning (§4.7) is two mechanisms claiming the camera at once, so this one
// claims it alone or not at all.
//
// The conversation itself is ordinary scene state painted by SceneDock; this
// component knows nothing about dialogue. It reads the pace off the ride and
// the world off rooms.js, and that is all it knows.

const lookAhead = 6;          // how far down the road the view leads the car
// How close the route must pass a venue for the reveal to move onto it (and for
// the drop-off to use the salon's real kerb). Past this, the authored point
// stands — a reveal for a building you cannot see is worse than one a little
// early.
const NEAR_ROUTE = 40;

// The route point (index ≥ 1 — never the pickup) nearest a world x/z.
function nearestRoutePoint(route, x, z) {
  let best = -1; let bd = Infinity;
  for (let i = 1; i < route.length; i++) {
    const d = Math.hypot(route[i][0] - x, route[i][1] - z);
    if (d < bd) { bd = d; best = i; }
  }
  return { index: best, dist: bd };
}
const seatBob = 0.035;        // barely-there vertical life so it reads as a car

export default function RideSequence({ eye = 0.75 }) {
  const { camera } = useThree();
  const ride = useWorldStore((s) => s.ride);
  const dropoff = useWorldStore((s) => s.rideDropoff);
  const run = useRef(null);

  useFrame((state, dt) => {
    if (!ride) { run.current = null; return; }

    const { route: authored, loopTo, reveals, dropoff: kerb } = INTRO_RIDE;
    if (!run.current) {
      // The ride starts at the ACTUAL car — the city's parked Elise
      // (store.landmark), with the authored point as fallback. Cloned so the
      // shared route data never mutates.
      const st = useWorldStore.getState();
      const lm = st.landmark;
      const route = [...authored];
      if (lm) route[0] = [lm[0], lm[2]];
      // ── THE TOUR SHOWS THE REAL BUILDINGS ─────────────────────────────────
      // The route is traced on the model city's streets, but its reveal points
      // were authored against the hand-built block's venue positions. In the
      // model city each venue stands on whatever building CityModel anchored it
      // to, so "the studio, lit up on the corner" could be announced beside a
      // laundrette. Each reveal now fires where the route passes closest to
      // that venue's actual anchor (when it passes close at all).
      const anchors = st.venueAnchors;
      const dyn = reveals.map((v) => {
        const a = anchors?.[v.key];
        if (!a) return v;
        const n = nearestRoutePoint(route, a[0], a[2]);
        return n.dist <= NEAR_ROUTE ? { ...v, atIndex: n.index } : v;
      });
      // Same for the drop-off: the salon kerb is wherever the salon really is.
      let drop = kerb;
      const sa = anchors?.salon;
      if (sa) {
        const n = nearestRoutePoint(route, sa[0], sa[2]);
        if (n.dist <= NEAR_ROUTE) drop = { pos: route[n.index], look: [sa[0], sa[2]] };
      }
      run.current = { seg: 0, into: 0, fired: new Set(), glide: null, t: 0, route, reveals: dyn, drop };
    }
    const route = run.current.route || authored;
    const r = run.current;
    r.t += dt;

    // ── The drop-off glide ──────────────────────────────────────────────────
    if (dropoff) {
      if (!r.glide) {
        const k2 = r.drop || kerb;
        const to = new THREE.Vector3(k2.pos[0], eye, k2.pos[1]);
        const m = new THREE.Matrix4().lookAt(to, new THREE.Vector3(k2.look[0], eye, k2.look[1]), new THREE.Vector3(0, 1, 0));
        r.glide = {
          fromPos: camera.position.clone(), toPos: to,
          fromQuat: camera.quaternion.clone(),
          toQuat: new THREE.Quaternion().setFromRotationMatrix(m),
          t: 0, dur: 2.6,
        };
      }
      const g = r.glide;
      g.t += dt;
      const k = Math.min(1, g.t / g.dur);
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      camera.position.lerpVectors(g.fromPos, g.toPos, e);
      camera.quaternion.slerpQuaternions(g.fromQuat, g.toQuat, e);
      emitMove([camera.position.x, camera.position.y, camera.position.z], camera.rotation.y);
      if (k >= 1) useWorldStore.getState().endRide();   // WalkControls re-adopts
      return;
    }

    // ── The tour loop ───────────────────────────────────────────────────────
    const speed = RIDE_SPEEDS[ride.pace] || RIDE_SPEEDS.touring;
    const next = (i) => (i + 1 < route.length ? i + 1 : loopTo);

    let step = speed * dt;
    let guard = 0;
    while (step > 0 && guard++ < route.length) {
      const a = route[r.seg], b = route[next(r.seg)];
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 0.001;
      const remain = len - r.into;
      if (step < remain) { r.into += step; step = 0; } else {
        step -= remain;
        r.seg = next(r.seg);
        r.into = 0;
        // A reveal fires the moment its route point is REACHED — once per
        // venue, ever (keyed by venue now that two can share a point).
        const hit = (r.reveals || reveals).find((v) => v.atIndex === r.seg && !r.fired.has(v.key));
        if (hit) {
          r.fired.add(hit.key);
          const loc = TOWN_LOCATIONS.find((l) => l.key === hit.key);
          if (loc) {
            useWorldStore.getState().setRideNote({
              key: loc.key, name: loc.name, accent: loc.accent, line: hit.line,
            });
          }
        }
      }
    }

    const a = route[r.seg], b = route[next(r.seg)];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 0.001;
    const k = r.into / len;
    const x = a[0] + (b[0] - a[0]) * k;
    const z = a[1] + (b[1] - a[1]) * k;
    camera.position.set(x, eye + 0.1 + Math.sin(r.t * 2.1) * seatBob, z);

    // Look a few units down the road (across segment joins), eased so corners
    // read as a car turning rather than a view snapping.
    let ahead = lookAhead; let s2 = r.seg; let into2 = r.into;
    let lx = b[0], lz = b[1];
    let guard2 = 0;
    while (ahead > 0 && guard2++ < route.length) {
      const p = route[s2], q = route[next(s2)];
      const l2 = Math.hypot(q[0] - p[0], q[1] - p[1]) || 0.001;
      const rem = l2 - into2;
      if (ahead < rem) {
        const kk = (into2 + ahead) / l2;
        lx = p[0] + (q[0] - p[0]) * kk; lz = p[1] + (q[1] - p[1]) * kk;
        ahead = 0;
      } else { ahead -= rem; s2 = next(s2); into2 = 0; lx = route[s2][0]; lz = route[s2][1]; }
    }
    const m = new THREE.Matrix4().lookAt(camera.position, new THREE.Vector3(lx, eye + 0.06, lz), new THREE.Vector3(0, 1, 0));
    const want = new THREE.Quaternion().setFromRotationMatrix(m);
    camera.quaternion.slerp(want, 1 - Math.exp(-3.2 * dt));

    // Peers watch the car go by (they floor-anchor, so the bob is ignored).
    emitMove([camera.position.x, camera.position.y, camera.position.z], camera.rotation.y);
  });

  return null;
}
